import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, CheckCircle, AlertCircle, Upload, Trash2, FileText, ChevronRight, ChevronLeft
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserCoach } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  fetchCoachConfig,
  saveCloneSetup,
  createVectorStore,
  uploadKnowledgeFile,
  listKnowledgeFiles,
  deleteKnowledgeFile,
  type CloneSetupData,
  type KnowledgeFile,
} from '../../lib/ai-coach';

const STEPS = [
  { id: 'name', title: 'Name & Purpose', description: 'What should your AI Coach be called?' },
  { id: 'style', title: 'Coaching Style', description: 'How does your AI Coach communicate?' },
  { id: 'principles', title: 'Principles', description: 'Your core coaching methodology' },
  { id: 'boundaries', title: 'Boundaries', description: 'What should your AI Coach avoid?' },
  { id: 'welcome', title: 'Welcome & Starters', description: 'First impression matters' },
  { id: 'knowledge', title: 'Knowledge Base', description: 'Upload your materials' },
  { id: 'access', title: 'Access & Review', description: 'Who can use your AI Coach?' },
];

export function AICoachSetupWizard() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [formData, setFormData] = useState<CloneSetupData>({
    cloneName: '',
    cloneDescription: '',
    coachingStyle: '',
    idealClient: '',
    principles: '',
    dosAndDonts: '',
    disclaimers: '',
    welcomeMessage: 'Welcome! How can I help you today?',
    starterQuestions: ['', '', ''],
    clientAccessEnabled: false,
  });

  const [knowledgeFiles, setKnowledgeFiles] = useState<KnowledgeFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingStore, setIsCreatingStore] = useState(false);
  const [hasVectorStore, setHasVectorStore] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }

    const init = async () => {
      try {
        const coach = await isUserCoach(user.id);
        if (!coach) { navigate('/login'); return; }

        const { data: coachRecord } = await supabase
          .from('coaches').select('id').eq('user_id', user.id).maybeSingle();
        if (!coachRecord) { navigate('/login'); return; }
        setCoachId(coachRecord.id);

        const config = await fetchCoachConfig(coachRecord.id);
        if (config) {
          setFormData({
            cloneName: config.cloneName,
            cloneDescription: config.cloneDescription,
            coachingStyle: config.coachingStyle,
            idealClient: config.idealClient,
            principles: config.principles,
            dosAndDonts: config.dosAndDonts,
            disclaimers: config.disclaimers,
            welcomeMessage: config.welcomeMessage,
            starterQuestions: config.starterQuestions.length > 0 ? config.starterQuestions : ['', '', ''],
            clientAccessEnabled: config.clientAccessEnabled,
          });
          setHasVectorStore(!!config.vectorStoreId);
        }

        try {
          const files = await listKnowledgeFiles();
          setKnowledgeFiles(files);
        } catch (_) { /* no files yet */ }
      } catch (err) {
        console.error('Error loading setup:', err);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [user, authLoading, navigate]);

  const updateField = (field: keyof CloneSetupData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateVectorStore = async () => {
    if (!coachId) return;
    setIsCreatingStore(true);
    try {
      await createVectorStore(coachId);
      setHasVectorStore(true);
      setToast({ type: 'success', message: 'Knowledge base created successfully' });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setIsCreatingStore(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      await uploadKnowledgeFile(file);
      const files = await listKnowledgeFiles();
      setKnowledgeFiles(files);
      setToast({ type: 'success', message: `Uploaded: ${file.name}` });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteKnowledgeFile(fileId);
      setKnowledgeFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const handleSave = async () => {
    if (!coachId) return;
    setIsSaving(true);
    try {
      const cleanedData = {
        ...formData,
        starterQuestions: formData.starterQuestions.filter(q => q.trim() !== ''),
      };
      await saveCloneSetup(coachId, cleanedData);
      setToast({ type: 'success', message: 'AI Coach Clone saved successfully!' });
      setTimeout(() => navigate('/coach/ai-settings'), 1500);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const canProceed = () => {
    if (currentStep === 0) return formData.cloneName.trim().length > 0;
    return true;
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading setup wizard..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-sage-500 rounded-xl shadow-soft">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">AI Coach Clone Setup</h1>
              <p className="freud-text-sm text-chocolate-500">
                Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].description}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <div className="flex gap-1.5">
          {STEPS.map((step, idx) => (
            <button
              key={step.id}
              onClick={() => setCurrentStep(idx)}
              className={`flex-1 h-2 rounded-full transition-all ${
                idx <= currentStep ? 'bg-sage-500' : 'bg-cream-300'
              }`}
            />
          ))}
        </div>
        <div className="flex justify-between mt-2">
          {STEPS.map((step, idx) => (
            <span key={step.id} className={`text-[10px] font-medium ${
              idx === currentStep ? 'text-sage-700' : 'text-chocolate-400'
            }`}>
              {step.title}
            </span>
          ))}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-2 p-4 rounded-xl mb-6 ${
              toast.type === 'success' ? 'bg-sage-100 text-sage-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {toast.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <p className="text-sm font-medium">{toast.message}</p>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="freud-card-elevated p-8"
          >
            {/* Step 0: Name & Purpose */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    AI Coach Clone Name *
                  </label>
                  <input
                    type="text"
                    value={formData.cloneName}
                    onChange={(e) => updateField('cloneName', e.target.value)}
                    placeholder="e.g. Coach Sarah, Mindset Mentor, Growth Guide"
                    className="freud-input text-lg"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">This is how your AI Coach will introduce itself</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    What does your AI Coach help with?
                  </label>
                  <textarea
                    value={formData.cloneDescription}
                    onChange={(e) => updateField('cloneDescription', e.target.value)}
                    placeholder="e.g. I help professionals unlock their full potential through mindset coaching, accountability, and personal development strategies."
                    rows={4}
                    className="freud-input resize-none"
                  />
                </div>
              </div>
            )}

            {/* Step 1: Coaching Style */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Coaching Style & Tone of Voice
                  </label>
                  <textarea
                    value={formData.coachingStyle}
                    onChange={(e) => updateField('coachingStyle', e.target.value)}
                    placeholder="e.g. Warm and encouraging but direct. Uses questions to provoke insight. Balances challenge with support. Speaks conversationally, avoids jargon."
                    rows={5}
                    className="freud-input resize-none"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">Describe how your AI Coach should communicate</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Ideal Client Type
                  </label>
                  <textarea
                    value={formData.idealClient}
                    onChange={(e) => updateField('idealClient', e.target.value)}
                    placeholder="e.g. Professionals aged 30-50 looking to advance their careers, improve leadership skills, or navigate major life transitions."
                    rows={3}
                    className="freud-input resize-none"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Principles */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Key Coaching Principles & Methodology
                  </label>
                  <textarea
                    value={formData.principles}
                    onChange={(e) => updateField('principles', e.target.value)}
                    placeholder={"e.g.\n- Growth mindset over fixed mindset\n- Small consistent actions compound\n- Self-awareness is the foundation of change\n- Challenge limiting beliefs with evidence\n- Celebrate progress, not just outcomes"}
                    rows={8}
                    className="freud-input resize-none"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">List your core beliefs and methods that guide your coaching</p>
                </div>
              </div>
            )}

            {/* Step 3: Boundaries */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Do's and Don'ts
                  </label>
                  <textarea
                    value={formData.dosAndDonts}
                    onChange={(e) => updateField('dosAndDonts', e.target.value)}
                    placeholder={"e.g.\nDO: Ask powerful questions, celebrate wins, suggest actionable steps\nDON'T: Give medical advice, diagnose conditions, make promises about outcomes, discuss other clients"}
                    rows={5}
                    className="freud-input resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Disclaimers & Boundaries
                  </label>
                  <textarea
                    value={formData.disclaimers}
                    onChange={(e) => updateField('disclaimers', e.target.value)}
                    placeholder="e.g. This AI Coach is for personal development purposes only. It does not replace professional therapy, counselling, or medical advice. If you are in crisis, please contact a qualified professional."
                    rows={4}
                    className="freud-input resize-none"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Welcome & Starters */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Welcome Message
                  </label>
                  <textarea
                    value={formData.welcomeMessage}
                    onChange={(e) => updateField('welcomeMessage', e.target.value)}
                    placeholder="e.g. Hi! I'm your personal AI Coach. I'm here to help you explore your potential and work towards your goals. What would you like to focus on today?"
                    rows={3}
                    className="freud-input resize-none"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">The first message users see when they open the chat</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Suggested Starter Questions
                  </label>
                  <p className="text-xs text-chocolate-400 mb-3">Give users ideas on how to start the conversation</p>
                  {formData.starterQuestions.map((q, idx) => (
                    <input
                      key={idx}
                      type="text"
                      value={q}
                      onChange={(e) => {
                        const updated = [...formData.starterQuestions];
                        updated[idx] = e.target.value;
                        updateField('starterQuestions', updated);
                      }}
                      placeholder={`Starter question ${idx + 1}`}
                      className="freud-input mb-2"
                    />
                  ))}
                  {formData.starterQuestions.length < 6 && (
                    <button
                      onClick={() => updateField('starterQuestions', [...formData.starterQuestions, ''])}
                      className="text-sm text-sage-600 hover:text-sage-700 font-medium"
                    >
                      + Add another question
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Step 5: Knowledge Base */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <p className="text-sm text-chocolate-600">
                  Upload documents, frameworks, or training materials that your AI Coach should reference.
                  Supported: PDF, TXT, DOCX, MD files.
                </p>

                {!hasVectorStore ? (
                  <div className="text-center py-8 border-2 border-dashed border-cream-300 rounded-xl">
                    <FileText className="w-10 h-10 text-cream-400 mx-auto mb-3" />
                    <p className="text-sm text-chocolate-500 mb-4">Set up your knowledge base to upload files</p>
                    <button
                      onClick={handleCreateVectorStore}
                      disabled={isCreatingStore}
                      className="freud-btn-primary"
                    >
                      {isCreatingStore ? 'Creating...' : 'Create Knowledge Base'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="border-2 border-dashed border-cream-300 rounded-xl p-6 text-center">
                      <Upload className="w-8 h-8 text-cream-400 mx-auto mb-2" />
                      <label className="cursor-pointer">
                        <span className="text-sm text-sage-600 font-semibold hover:text-sage-700">
                          {isUploading ? 'Uploading...' : 'Click to upload a file'}
                        </span>
                        <input
                          type="file"
                          onChange={handleFileUpload}
                          disabled={isUploading}
                          accept=".pdf,.txt,.docx,.md,.csv"
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-chocolate-400 mt-1">Max 50MB per file</p>
                    </div>

                    {knowledgeFiles.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-chocolate-700">Uploaded Files</h4>
                        {knowledgeFiles.map(file => (
                          <div key={file.id} className="flex items-center justify-between py-2.5 px-4 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-chocolate-400" />
                              <div>
                                <p className="text-sm font-medium text-chocolate-700">{file.filename}</p>
                                <p className="text-xs text-chocolate-400">
                                  {(file.file_size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteFile(file.id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 6: Access & Review */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-cream-50 rounded-xl border border-cream-200">
                  <div>
                    <p className="text-sm font-semibold text-chocolate-800">Client Access</p>
                    <p className="text-xs text-chocolate-500">Allow your respondents/clients to chat with your AI Coach</p>
                  </div>
                  <button
                    onClick={() => updateField('clientAccessEnabled', !formData.clientAccessEnabled)}
                    className="group flex items-center"
                  >
                    <div className={`relative w-14 h-7 rounded-full transition-colors ${
                      formData.clientAccessEnabled ? 'bg-sage-500' : 'bg-gray-300'
                    }`}>
                      <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                        formData.clientAccessEnabled ? 'translate-x-[30px]' : 'translate-x-0.5'
                      }`} />
                    </div>
                  </button>
                </div>

                <div className="p-4 bg-sage-50 rounded-xl border border-sage-200">
                  <h4 className="text-sm font-semibold text-sage-800 mb-3">Setup Summary</h4>
                  <div className="space-y-2 text-sm text-sage-700">
                    <p><span className="font-medium">Name:</span> {formData.cloneName || '(not set)'}</p>
                    <p><span className="font-medium">Purpose:</span> {formData.cloneDescription ? formData.cloneDescription.slice(0, 80) + '...' : '(not set)'}</p>
                    <p><span className="font-medium">Style:</span> {formData.coachingStyle ? 'Configured' : '(not set)'}</p>
                    <p><span className="font-medium">Principles:</span> {formData.principles ? 'Configured' : '(not set)'}</p>
                    <p><span className="font-medium">Boundaries:</span> {formData.dosAndDonts || formData.disclaimers ? 'Configured' : '(not set)'}</p>
                    <p><span className="font-medium">Knowledge files:</span> {knowledgeFiles.length} uploaded</p>
                    <p><span className="font-medium">Client access:</span> {formData.clientAccessEnabled ? 'Enabled' : 'Disabled'}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
            disabled={currentStep === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-chocolate-600 font-semibold rounded-xl hover:bg-cream-100 transition-all disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-sage-600 text-white font-semibold rounded-xl hover:bg-sage-700 transition-all disabled:opacity-50"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={isSaving || !formData.cloneName.trim()}
              className="inline-flex items-center gap-2 px-8 py-2.5 bg-sage-600 text-white font-semibold rounded-xl hover:bg-sage-700 transition-all disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save & Activate'}
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
